import { useState } from "react";
import { GameInfoService } from "../ApiGame";
function percentToRating(percent: number) {
  if (percent < 20) return 0;
  if (percent < 40) return 1;
  if (percent < 60) return 2;
  if (percent < 80) return 3;
  if (percent < 100) return 4;
  return 5; // 80% 及以上
}

export type FillSignal = { color: "green" | "yellow" | "red"; bars: number };

// Heat bars keyed to fill `percentage` (0–100, the buildGroup scale). Replaces the old
// `chances` (bet-coverage) binding and the dead `process` rating — colour + bar-count now
// track how full the bolão is. Band boundaries are covered by getFillSignal.test.ts.
export function getFillSignal(percentage: number): FillSignal {
  if (percentage < 50) return { color: "green", bars: 2 };
  if (percentage < 80) return { color: "yellow", bars: 3 };
  if (percentage < 90) return { color: "red", bars: 4 };
  return { color: "red", bars: 5 };
}


export const buildGameInfo = (data: any) => {
  let d = {
    gameTag: getGameIdByName(data.name),
    gameId: data.id,
    No: data.game_id,
    gameName: data.name === 'LOTOFACÍL' || data.name === 'LOTOFÁCIL' || data.name === 'Lotofacil' ? 'Lotofácil' : data.name,
    description: data.description,
    imgUrl: "",
    draw: null as any,
  }
  d.imgUrl = data.icon || "/images/" + d.gameTag + ".png";
  return d;
}
export const buildDrawInfo = (data: any, game?: any) => {
  let d = {
    drawId: data.id,
    drawNumber: data.number,
    No: data.stop_selling_time.slice(0, 10).replaceAll("-", ''),
    peopleNum: data.number,
    mainPrize: parseFloat(data.jackpot) > 0 ? data.jackpot : null,
    stopTime: new Date(data.stop_selling_time).getTime(),
    isStop: new Date(data.stop_selling_time).getTime() < Date.now(),
    isToday: isTimestampToday(new Date(data.draw_time).getTime()),
    detail: null as any,
    minNumbers: data.min_numbers + 1,
  }
  return d
}
export const buildDrawAll = (data: any) => {
  let d = buildDrawInfo(data);
  d.detail = {
    totalNumbers: data.total_numbers,
    minNumbers: data.choose_numbers_count_min,
    maxNumbers: data.choose_numbers_count_max,
    winMinNumbers: data.win_numbers_count_min,
    winMaxNumbers: data.win_numbers_count_max,
    priceRule: data.prices,
  }
  return d
}
export const buildGroupDetail = (data: any, draw?: any) => {
  let d = buildGroup(data) as any;
  d.quota = {
    maxQuota: data.max_quota,
    pricePerQuota: data.price_per_quota,
    joinedQuota: data.joined_quota,
    reservedQuota: data.reserved_quota ?? 0,   // null/legacy = no reserved cotas
  }
  if (draw) {
    d.draw = draw;
  }
  return d;
}
const getChances = (minChoose: number, maxChoose: number, numbersLength: number) => {
  console.log(minChoose, maxChoose, numbersLength);
  // 先判断边界情况，提前返回
  if (numbersLength <= minChoose) return 0;
  if (numbersLength >= maxChoose) return 4;
  
  // 将 [minChoose, maxChoose] 范围分成 5 段
  const range = maxChoose - minChoose + 1; // 总范围大小
  const base = Math.floor(range / 5); // 每段基准大小
  const remainder = range % 5; // 余数
  
  // 计算 numbersLength 在范围内的相对位置
  const relativePos = numbersLength - minChoose;
  
  // 计算每段的起始位置和大小
  let segmentStart = 0;
  for (let i = 0; i < 5; i++) {
    const segmentSize = i < remainder ? base + 1 : base; // 前 remainder 段多分配 1
    const segmentEnd = segmentStart + segmentSize - 1;
    
    // 如果 numbersLength 落在当前段内，返回段索引
    if (relativePos >= segmentStart && relativePos <= segmentEnd) {
      return i;
    }
    
    segmentStart += segmentSize;
  }
  
  return 0;
}
// Normalized shape of one bolão card item (buildGroup output). Replaces the
// untyped `any` that list/card consumers used to rely on. Fields sourced from a
// possibly-absent API key are nullable so pre-backfill payloads stay type-safe.
export interface GroupCardItem {
  groupId: number;
  No: number;
  drawNumber: number | null;
  displayName: string;
  stopTime: number;
  groupPrice: number;
  peopleNum: number;
  percentage: number;
  process: number;
  numbersLength: number;
  numbers: any[];
  isFull: boolean;
  maxQuota: number | null;
  saleMode: "fractional" | "whole" | null;
  joinedQuota: number | null;
  reservedQuota: number;
  onlineAvailable: number | null;
  game: any;
  minChoose: number;
  maxChoose: number;
  chances: number;
  // chunk 13 — coverage signal from the list serializer; null on bolões without
  // computed strength (legacy / single-game / pre-backfill).
  jogoCount: number | null;
  headlineMultiplier: number | null;
  minPrizeTier: number | null;
}
export const buildGroup = (data: any, game?: any, index?: number): GroupCardItem => {
  const gameObj = game || buildGameInfo(data.game);
  const drawNumber = data.draw_number ?? data.draw?.number;
  const suffix = index !== undefined ? `-${String.fromCharCode(65 + index)}` : '';
  const displayName = drawNumber
    ? `${gameObj.gameName} #${drawNumber}${suffix}`
    : gameObj.gameName;
  let d = {
    groupId: data.group_id,
    No: data.id,
    drawNumber,
    displayName,
    stopTime: (new Date(data.stop_selling_time)).getTime(),
    groupPrice: data.price_per_quota,
    peopleNum: data.participants,
    percentage: +((data.percentage * 100).toFixed(2)),
    process: percentToRating(data.percentage * 100),
    numbersLength: data.numbers.length,
    numbers: data.numbers,
    isFull: data.percentage >= 100,
    maxQuota: data.max_quota ?? null,
    saleMode: data.sale_mode ?? null,   // 'fractional' | 'whole'; null on legacy payloads → isSingleCota falls back to maxQuota
    joinedQuota: data.joined_quota ?? null,
    reservedQuota: data.reserved_quota ?? 0,   // null/legacy = 0; list serializer omits it → 0
    onlineAvailable: data.online_available ?? null,   // server-computed (PR-A, Option B); null until deployed → cards degrade gracefully
    game: game || buildGameInfo(data.game),
    minChoose: data.game.choose_numbers_count_min,
    maxChoose: data.game.choose_numbers_count_max,
    chances: getChances(data.game.choose_numbers_count_min, data.game.choose_numbers_count_max, data.numbers.length),
    // chunk 13 — coverage signal; null-coalesced so pre-backfill bolões degrade cleanly (no null math downstream)
    jogoCount: data.jogo_count ?? null,
    headlineMultiplier: data.headline_multiplier ?? null,
    minPrizeTier: data.min_prize_tier ?? null,
  }
  return d
}


export const getGameIdByName = (name: string) => {
  switch (name) {
    case "Mega-Sena":
      return "mega";
    case "Lotofacil":
      return "loto";
    case "Quina":
      return "quina";
    case "Mega da Virada":
      return "mega";
    default:
      return "loto";
  }
}

const isTimestampToday = (timestamp: number) => {
  const now = new Date();          // 当前时间
  const target = new Date(timestamp); // 目标时间

  return (
    target.getFullYear() === now.getFullYear() &&
    target.getMonth() === now.getMonth() &&
    target.getDate() === now.getDate()
  );
}
export const getStyleTime = (timestamp: number | Date): string => {
  const date = timestamp instanceof Date ? timestamp : new Date(timestamp);
  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  const intervals = {
    year: 31536000,
    month: 2592000,
    week: 604800,
    day: 86400,
    hour: 3600,
    minute: 60,
    second: 1
  };

  if (diffInSeconds < 30) {
    return "now";
  }
  const formatDate = (date: Date, showYear: boolean): string => {
    const month = date.getMonth() + 1;
    const day = date.getDate();

    if (showYear) {
      return `${day}/${month}/${date.getFullYear()}`;
    }

    return `${day}/${month}`;
  };
  for (const [unit, secondsInUnit] of Object.entries(intervals)) {
    const count = Math.floor(diffInSeconds / secondsInUnit);

    if (count >= 1) {
      if (unit === 'month' && count > 6) {
        return formatDate(date, now.getFullYear() !== date.getFullYear());
      }

      if (unit === 'month' || unit === 'week' || unit === 'day') {
        if (count > 1) {
          return formatDate(date, now.getFullYear() !== date.getFullYear());
        }
      }

      return `${count} ${unit}`;
    }
  }

  return formatDate(date, now.getFullYear() !== date.getFullYear());
};




export const buildBetList = (number = 60) => {
  const d = Array.from({ length: number }, (_, i) => ({
    number: (i + 1).toString(),
    type: "default",
  }));
  return d;
};

export function useDraw() {
  const [draw, setDraw] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<any>(null);

  const fetchDraw = async (id: string) => {
    setLoading(true);
    const response = await GameInfoService.getDraw(id);
    if (response.code === 0) {
      setDraw({
        game: buildGameInfo(response.data.game),
        draw: buildDrawAll(response.data)
      });
    } else {
      setError(response.message);
    }
    setLoading(false);
  };

  return { draw, fetchDraw, loading, error };
}
// ── Stale-while-revalidate cache for the unfiltered /api/game/hot/ payload ──
// The home carousel's banner URLs derive from this response, so hydrating from
// cache puts the LCP image in the first render instead of after an uncached
// round trip to the API (docs/perf/phase4_diagnosis.md, Fix 4).
// The RAW payload is cached, not the built list: time-derived fields (isStop,
// isToday) are recomputed at hydrate time, so a draw that closed while cached
// still renders as closed.
const GAME_HOT_CACHE_KEY = "lvs_game_hot_cache_v1"; // bump suffix on payload shape change
const GAME_HOT_CACHE_MAX_AGE_MS = 60 * 60 * 1000;

const readGameHotCache = (): any[] | null => {
  try {
    const raw = localStorage.getItem(GAME_HOT_CACHE_KEY);
    if (!raw) return null;
    const cached = JSON.parse(raw);
    if (!cached || typeof cached.ts !== "number" || !Array.isArray(cached.data)) return null;
    if (Date.now() - cached.ts > GAME_HOT_CACHE_MAX_AGE_MS) return null;
    return cached.data;
  } catch {
    return null;
  }
};

const writeGameHotCache = (data: any[]) => {
  try {
    localStorage.setItem(GAME_HOT_CACHE_KEY, JSON.stringify({ ts: Date.now(), data }));
  } catch {
    // quota exceeded / private mode — cache is best-effort
  }
};

const buildHotGameList = (data: any[]) =>
  data.map((item: any) => {
    const game = buildGameInfo(item);
    return ({
      game: game,
      popularGroups: item.popular_groups.map((group: any, index: number) => buildGroup(group, game, index)),
      draw: item.next_draw.length > 0
        ? buildDrawInfo(item.next_draw[0])
        : item.last_jackpot
          ? {
              drawId: null,
              No: '',
              peopleNum: item.last_jackpot.number,
              mainPrize: item.last_jackpot.jackpot,
              stopTime: new Date(item.last_jackpot.stop_selling_time).getTime(),
              isStop: true,
              isToday: false,
              detail: null,
              minNumbers: null,
              isClosed: true,
            }
          : null
    })
  });

export function useGameList() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<any>(null);
  // Hydrate from cache in the initializer (not an effect) so cached games are
  // in the FIRST render — the carousel and its banner image URLs paint before
  // the API round trip instead of after it.
  const [gameList, setGameList] = useState<any[]>(() => {
    try {
      const cached = readGameHotCache();
      return cached ? buildHotGameList(cached) : [];
    } catch {
      return [];
    }
  });
  const fetchGameList = async (gameId?: any) => {
    // Unfiltered fetch with data already on screen = background revalidation:
    // no skeleton over real cards, fresh data swaps in when it lands.
    const silent = !gameId && gameList.length > 0;
    if (!silent) setLoading(true);
    let params = {};
    if (gameId) {
      params = {
        id: gameId,
      }
    }
    const response = await GameInfoService.getGameList(params);
    if (response.code === 0) {
      setGameList(buildHotGameList(response.data));
      if (!gameId) writeGameHotCache(response.data);
    } else if (gameList.length === 0) {
      // Surface errors only on a blank first load; a failed background
      // revalidation keeps the stale (cache-hydrated) list — stale beats blank.
      setError(response.code);
    }
    setLoading(false);
    return response;
  };


  return { gameList, fetchGameList, loading, error };
}